import React from 'react';
import { StudyModule, SkillSlide } from '../types';
import { PrintScope } from './PrintStudyGuideModal';

interface PrintStudyDocumentProps {
  modules?: StudyModule[];
  skills?: SkillSlide[];
  selectedModule?: StudyModule | null;
  selectedSkill?: SkillSlide | null;
  scope: PrintScope;
  certName?: string;
  renderModuleSlide?: (module: StudyModule, slide: any, index: number, setExpandedImage: any, isPrintMode: boolean) => React.ReactNode;
}

export const PrintStudyDocument: React.FC<PrintStudyDocumentProps> = ({
  modules = [],
  skills = [],
  selectedModule,
  selectedSkill,
  scope,
  certName = "Nurse Aide Certification (CNA)",
  renderModuleSlide
}) => {
  const shouldRenderStudyGuide = scope === 'study_guide' || scope === 'combined' || (scope === 'current' && !!selectedModule);
  const shouldRenderSkillsLab = scope === 'skills_lab' || scope === 'combined' || (scope === 'current' && !!selectedSkill);

  const displayModules = (scope === 'current' && selectedModule) ? [selectedModule] : modules;
  const displaySkills = (scope === 'current' && selectedSkill) ? [selectedSkill] : skills;

  return (
    <div id="print-study-document" className="hidden print:block w-full bg-white text-slate-900 p-0 m-0 font-sans leading-relaxed">
      {/* HEADER / COVER SHEET */}
      <div className="p-8 mb-8 border-b-4 border-slate-900">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-[10px] font-black tracking-[0.2em] text-slate-500 uppercase mb-1">
              DIAMOND SCHOOL OF NURSING & CLINICAL REGISTRY HUB
            </p>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-tight">
              {scope === 'combined'
                ? 'Complete Clinical Mastery Handbook & Study Guide'
                : scope === 'skills_lab'
                ? 'Clinical Skills Procedure Manual & Checklists'
                : scope === 'current' && selectedModule
                ? `Study Guide: ${selectedModule.title}`
                : scope === 'current' && selectedSkill
                ? `Skills Manual: ${selectedSkill.name}`
                : 'Theoretical Study Guide & Board Reasoning Syllabus'}
            </h1>
            <p className="text-xs font-bold text-indigo-700 mt-2 tracking-wide uppercase">
              {certName} — 2026 Examination Preparation Guidelines
            </p>
          </div>
          <div className="text-right border-l-2 border-slate-200 pl-4 py-1">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Official Review Document</p>
            <p className="text-[10px] font-bold text-slate-600 mt-1">Verified Board Content</p>
            <p className="text-[9px] text-slate-400 mt-1">{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>

        {/* CANDIDATE SIGNATURE BLOCK FOR BINDER & SKILL EVALUATION */}
        <div className="mt-8 pt-6 border-t border-slate-200 grid grid-cols-3 gap-6 text-[10px] font-bold text-slate-700">
          <div>
            <span className="text-slate-400 block text-[8px] uppercase tracking-widest font-black">Candidate Name</span>
            <div className="border-b border-slate-400 h-6 mt-1" />
          </div>
          <div>
            <span className="text-slate-400 block text-[8px] uppercase tracking-widest font-black">Registry ID / Date</span>
            <div className="border-b border-slate-400 h-6 mt-1" />
          </div>
          <div>
            <span className="text-slate-400 block text-[8px] uppercase tracking-widest font-black">Evaluator Signature</span>
            <div className="border-b border-slate-400 h-6 mt-1" />
          </div>
        </div>
      </div>

      {/* SECTION 1: THEORETICAL STUDY GUIDE */}
      {shouldRenderStudyGuide && displayModules.length > 0 && (
        <div className="mb-12">
          {/* Syllabus Catalog Table (rendered if multi-module) */}
          {displayModules.length > 1 && (
            <div className="mb-10 px-4">
              <h2 className="text-xl font-black uppercase text-slate-900 tracking-tight mb-4 border-b-2 border-indigo-600 pb-2">
                Theoretical Curriculum Syllabus ({displayModules.length} Modules)
              </h2>
              <table className="w-full text-left text-xs border-collapse border border-slate-300">
                <thead>
                  <tr className="bg-slate-100 text-slate-900 border-b border-slate-300 font-black uppercase text-[9px] tracking-wider">
                    <th className="p-2 border border-slate-300">#</th>
                    <th className="p-2 border border-slate-300">Module Title</th>
                    <th className="p-2 border border-slate-300">Sections</th>
                    <th className="p-2 border border-slate-300">Focus Area</th>
                    <th className="p-2 border border-slate-300 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayModules.map((mod, idx) => (
                    <tr key={mod.id || idx} className="border-b border-slate-200">
                      <td className="p-2 border border-slate-300 font-bold">{idx + 1}</td>
                      <td className="p-2 border border-slate-300 font-bold text-slate-900">{mod.title}</td>
                      <td className="p-2 border border-slate-300">{mod.slides?.length || 0} Sections</td>
                      <td className="p-2 border border-slate-300 font-medium">Board Reasoning</td>
                      <td className="p-2 border border-slate-300 text-center font-bold">[ &nbsp; ] Complete</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Detailed Module Content */}
          {displayModules.map((mod, mIdx) => (
            <div key={mod.id || mIdx} className={mIdx > 0 ? "print-page-break pt-8" : "pt-4"}>
              <div className="border-b-2 border-indigo-600 pb-3 mb-6 flex justify-between items-end">
                <div>
                  <span className="text-[9px] font-black text-indigo-600 tracking-widest uppercase">
                    Study Module {mIdx + 1} of {displayModules.length}
                  </span>
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight mt-1">{mod.title}</h2>
                </div>
                <span className="text-[9px] font-bold text-slate-400">Total Sections: {mod.slides?.length || 0}</span>
              </div>

              {/* Module Slides */}
              {mod.slides && mod.slides.map((slide, sIdx) => (
                <div key={slide.id || sIdx} className="mb-8 print-no-cut">
                  <div className="flex justify-between items-center border-b border-slate-200 pb-2 mb-4">
                    <span className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest">
                      Section {sIdx + 1}: {slide.title || slide.type}
                    </span>
                    <span className="text-[8px] font-bold text-slate-400 uppercase">{mod.title}</span>
                  </div>

                  {renderModuleSlide ? (
                    renderModuleSlide(mod, slide, sIdx, () => {}, true)
                  ) : (
                    <div className="p-4 border border-slate-200 rounded-xl bg-slate-50 space-y-2">
                      <h4 className="text-sm font-bold text-slate-900">{slide.title || 'Lesson Slide'}</h4>
                      {slide.data?.summary && <p className="text-xs text-slate-700">{slide.data.summary}</p>}
                      {slide.data?.takeaway && <p className="text-xs font-semibold text-indigo-900">{slide.data.takeaway}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* SECTION 2: CLINICAL SKILLS LAB MANUAL */}
      {shouldRenderSkillsLab && displaySkills.length > 0 && (
        <div className={shouldRenderStudyGuide ? "print-page-break pt-8" : "pt-4"}>
          {/* Skills Master Index (rendered if multi-skill) */}
          {displaySkills.length > 1 && (
            <div className="mb-10 px-4 print-no-cut">
              <h2 className="text-xl font-black uppercase text-slate-900 tracking-tight mb-4 border-b-2 border-teal-600 pb-2">
                Clinical Skills Procedure Master Index ({displaySkills.length} Procedures)
              </h2>
              <table className="w-full text-left text-xs border-collapse border border-slate-300">
                <thead>
                  <tr className="bg-slate-100 text-slate-900 border-b border-slate-300 font-black uppercase text-[9px] tracking-wider">
                    <th className="p-2 border border-slate-300">#</th>
                    <th className="p-2 border border-slate-300">Clinical Skill Name</th>
                    <th className="p-2 border border-slate-300">Total Steps</th>
                    <th className="p-2 border border-slate-300">Critical Steps</th>
                    <th className="p-2 border border-slate-300 text-center">Evaluator Sign-Off</th>
                  </tr>
                </thead>
                <tbody>
                  {displaySkills.map((sk, idx) => {
                    const procSlides = sk.slides?.filter(s => s.type === 'procedure') || [];
                    let stepsCount = 0;
                    procSlides.forEach(ps => {
                      stepsCount += ps.data?.steps?.length || 0;
                    });
                    const criticalsSlide = sk.slides?.find(s => s.type === 'criticals');
                    const criticalsCount = (criticalsSlide?.data?.items?.length || 0) +
                      procSlides.reduce((acc, ps) => acc + (ps.data?.steps?.filter((s: any) => s.isCritical || s.critical)?.length || 0), 0);
                    
                    return (
                      <tr key={sk.id || idx} className="border-b border-slate-200">
                        <td className="p-2 border border-slate-300 font-bold">{idx + 1}</td>
                        <td className="p-2 border border-slate-300 font-bold text-slate-900">{sk.name}</td>
                        <td className="p-2 border border-slate-300">{stepsCount} Steps</td>
                        <td className="p-2 border border-slate-300 font-bold text-rose-700">{criticalsCount} Critical</td>
                        <td className="p-2 border border-slate-300 text-center font-bold">[ &nbsp; ] Pass &nbsp; [ &nbsp; ] Fail</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Skill-by-Skill Comprehensive Manual */}
          {displaySkills.map((sk, sIdx) => {
            const overviewSlide = sk.slides?.find(s => s.type === 'overview');
            const suppliesSlide = sk.slides?.find(s => s.type === 'supplies');
            const procedureSlides = sk.slides?.filter(s => s.type === 'procedure') || [];
            const criticalsSlide = sk.slides?.find(s => s.type === 'criticals');
            const reportingSlide = sk.slides?.find(s => s.type === 'reporting');
            const completionSlide = sk.slides?.find(s => s.type === 'completion');
            const infographicSlides = sk.slides?.filter(s => s.type === 'infographic') || [];

            const supplies = suppliesSlide?.data?.items || suppliesSlide?.data?.supplies || [];

            return (
              <div key={sk.id || sIdx} className={sIdx > 0 ? "print-page-break pt-8" : "pt-4"}>
                {/* Skill Header */}
                <div className="border-b-2 border-teal-600 pb-3 mb-6 flex justify-between items-end print-no-cut">
                  <div>
                    <span className="text-[9px] font-black text-teal-700 tracking-widest uppercase">
                      Clinical Skill Procedure {sIdx + 1} of {displaySkills.length}
                    </span>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight mt-1">{sk.name}</h2>
                  </div>
                  <div className="text-right">
                    <span className="text-[9px] font-bold text-slate-500 uppercase block">State Registry Exam Standard</span>
                    <span className="text-[10px] font-black text-teal-700">[ &nbsp; ] Approved Evaluation Checklist</span>
                  </div>
                </div>

                {/* Overview / Clinical Rationale */}
                {overviewSlide?.data && (
                  <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-xl print-no-cut space-y-3">
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Clinical Significance & Board Focus</h4>
                    {overviewSlide.data.importance && (
                      <p className="text-xs text-slate-800 font-medium leading-relaxed">{overviewSlide.data.importance}</p>
                    )}
                    <div className="grid grid-cols-2 gap-3 pt-2 text-[11px]">
                      {overviewSlide.data.examContext && (
                        <div className="p-2.5 bg-indigo-50/60 border border-indigo-200 rounded-lg">
                          <span className="font-bold text-indigo-900 uppercase text-[9px] block mb-0.5">Board Objective:</span>
                          <p className="text-slate-800 font-medium">{overviewSlide.data.examContext}</p>
                        </div>
                      )}
                      {overviewSlide.data.safetyFocus && (
                        <div className="p-2.5 bg-rose-50/60 border border-rose-200 rounded-lg">
                          <span className="font-bold text-rose-900 uppercase text-[9px] block mb-0.5">Safety Focus:</span>
                          <p className="text-slate-800 font-medium">{overviewSlide.data.safetyFocus}</p>
                        </div>
                      )}
                    </div>
                    {overviewSlide.data.clinicalTips && (
                      <div className="p-2.5 bg-amber-50/60 border border-amber-200 rounded-lg text-[11px]">
                        <span className="font-bold text-amber-900 uppercase text-[9px] block mb-0.5">Nurse Educator Pro Tip:</span>
                        <p className="text-slate-800 italic">{overviewSlide.data.clinicalTips}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Equipment & Supplies */}
                {supplies.length > 0 && (
                  <div className="mb-6 p-4 border border-slate-300 rounded-xl print-no-cut">
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider mb-2">Required Supplies & Clinical Inventory</h4>
                    <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs font-medium text-slate-800">
                      {supplies.map((item: string, i: number) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="w-3.5 h-3.5 border border-slate-500 rounded-xs inline-block shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Step-by-Step Procedure Phases */}
                {procedureSlides.map((pSlide, pIdx) => {
                  const pSteps = pSlide.data?.steps || [];
                  return (
                    <div key={pSlide.id || pIdx} className="mb-8 print-no-cut space-y-3">
                      <div className="flex justify-between items-center border-b border-slate-300 pb-1.5">
                        <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">
                          {pSlide.title || `Phase ${pIdx + 1} Step-by-Step Action Guidelines`}
                        </h4>
                        <span className="text-[9px] font-bold text-slate-500 uppercase">Phase {pIdx + 1} of {procedureSlides.length}</span>
                      </div>

                      {pSlide.data?.imageUrl && (
                        <div className="my-3 text-center print-no-cut">
                          <img
                            src={pSlide.data.imageUrl}
                            alt={pSlide.data.visualAlt || "Procedure step illustration"}
                            className="print-image-fit max-h-[250px] object-contain mx-auto rounded-lg border border-slate-300"
                          />
                        </div>
                      )}

                      {pSteps.length > 0 && (
                        <table className="w-full text-left text-xs border-collapse border border-slate-300">
                          <thead>
                            <tr className="bg-slate-100 text-slate-900 border-b border-slate-300 font-black uppercase text-[9px] tracking-wider">
                              <th className="p-2 border border-slate-300 w-8 text-center">#</th>
                              <th className="p-2 border border-slate-300">Procedural Action Step</th>
                              <th className="p-2 border border-slate-300">Clinical Rationale</th>
                              <th className="p-2 border border-slate-300 w-24 text-center">Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pSteps.map((stepItem: any, stIdx: number) => {
                              const isObj = typeof stepItem === 'object' && stepItem !== null;
                              const stepTitle = isObj ? stepItem.title : null;
                              const stepText = isObj ? (stepItem.text || stepItem.action || stepItem.title) : String(stepItem);
                              const stepRationale = isObj ? (stepItem.rationale || stepItem.reason) : "Ensures patient safety, dignity, and infection control.";
                              const isCritical = isObj && (stepItem.isCritical || stepItem.critical || stepItem.type === 'critical');

                              return (
                                <tr key={stIdx} className={`border-b border-slate-200 ${isCritical ? 'bg-rose-50/60' : ''}`}>
                                  <td className="p-2 border border-slate-300 font-black text-center">{stIdx + 1}</td>
                                  <td className="p-2 border border-slate-300">
                                    <div className="space-y-1">
                                      {stepTitle && <p className="font-bold text-slate-900">{stepTitle}</p>}
                                      <p className="text-slate-900 font-medium leading-relaxed">"{stepText}"</p>
                                      {isCritical && (
                                        <span className="inline-block px-1.5 py-0.5 bg-rose-600 text-white font-black text-[8px] uppercase tracking-wider rounded-xs mt-1">
                                          ★ CRITICAL SAFETY STEP
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-2 border border-slate-300 text-slate-600 text-[11px] font-medium leading-snug">
                                    {stepRationale}
                                  </td>
                                  <td className="p-2 border border-slate-300 text-center font-bold text-[10px] whitespace-nowrap">
                                    [ &nbsp; ] Pass &nbsp; [ &nbsp; ] Fail
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}

                      {/* Advanced rationale/tips on procedure slide */}
                      {(pSlide.data?.clinicalRationale || pSlide.data?.instructorTip || pSlide.data?.criticalPoints) && (
                        <div className="grid grid-cols-2 gap-3 pt-1 text-[11px]">
                          {pSlide.data.clinicalRationale && (
                            <div className="p-2.5 bg-indigo-50/50 border border-indigo-200 rounded-lg">
                              <span className="font-bold text-indigo-900 uppercase text-[9px] block">Nurse Rationale:</span>
                              <p className="text-slate-800 italic">"{pSlide.data.clinicalRationale}"</p>
                            </div>
                          )}
                          {pSlide.data.instructorTip && (
                            <div className="p-2.5 bg-emerald-50/50 border border-emerald-200 rounded-lg">
                              <span className="font-bold text-emerald-900 uppercase text-[9px] block">Instructor Tip:</span>
                              <p className="text-slate-800 font-medium">"{pSlide.data.instructorTip}"</p>
                            </div>
                          )}
                          {pSlide.data.criticalPoints && (
                            <div className="col-span-2 p-2.5 bg-rose-50/60 border border-rose-300 rounded-lg">
                              <span className="font-bold text-rose-900 uppercase text-[9px] block">Critical Failure Warning:</span>
                              <p className="text-slate-900 font-bold">{pSlide.data.criticalPoints}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Critical Safety Thresholds */}
                {criticalsSlide?.data?.items && criticalsSlide.data.items.length > 0 && (
                  <div className="mb-6 p-4 bg-rose-50/50 border-2 border-rose-300 rounded-xl print-no-cut">
                    <h4 className="text-xs font-black text-rose-900 uppercase tracking-wider mb-2">
                      Critical Safety Thresholds (Automatic Failure Criteria)
                    </h4>
                    <ul className="space-y-1.5 text-xs font-bold text-rose-950">
                      {criticalsSlide.data.items.map((item: string, cIdx: number) => (
                        <li key={cIdx} className="flex items-start gap-2">
                          <span className="text-rose-600 font-black">★</span>
                          <span>"{item}"</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Reporting & Documentation Standard */}
                {reportingSlide?.data && (
                  <div className="mb-6 p-4 bg-slate-50 border border-slate-300 rounded-xl print-no-cut">
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider mb-1">
                      Verbal Reporting & Documentation Standard
                    </h4>
                    {reportingSlide.data.instruction && (
                      <p className="text-xs text-slate-700 font-medium mb-2">{reportingSlide.data.instruction}</p>
                    )}
                    {reportingSlide.data.sampleWording && (
                      <div className="p-3 bg-slate-900 text-emerald-400 font-mono text-[11px] rounded-lg">
                        <span className="text-slate-400 font-sans font-bold block text-[9px] uppercase mb-0.5">Accurate Verbal Report Wording:</span>
                        "{reportingSlide.data.sampleWording}"
                      </div>
                    )}
                  </div>
                )}

                {/* Completion Criteria */}
                {completionSlide?.data?.items && completionSlide.data.items.length > 0 && (
                  <div className="mb-6 p-4 bg-emerald-50/40 border border-emerald-300 rounded-xl print-no-cut">
                    <h4 className="text-xs font-black text-emerald-900 uppercase tracking-wider mb-2">
                      Procedure Completion Criteria
                    </h4>
                    <ul className="grid grid-cols-2 gap-2 text-xs font-bold text-emerald-950">
                      {completionSlide.data.items.map((item: string, compIdx: number) => (
                        <li key={compIdx} className="flex items-center gap-2">
                          <span className="w-3 h-3 border border-emerald-600 rounded-xs inline-block shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Infographic / Visual Observations */}
                {infographicSlides.map((infoSlide, infoIdx) => (
                  <div key={infoSlide.id || infoIdx} className="mb-6 p-4 border border-slate-300 rounded-xl print-no-cut">
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider mb-2">
                      {infoSlide.title || "Visual Observation & Diagnostic Checklist"}
                    </h4>
                    {infoSlide.data?.imageUrl && (
                      <div className="mb-3 text-center">
                        <img
                          src={infoSlide.data.imageUrl}
                          alt={infoSlide.data.visualAlt || "Visual observation illustration"}
                          className="print-image-fit max-h-[250px] object-contain mx-auto rounded-lg border border-slate-300"
                        />
                      </div>
                    )}
                    {infoSlide.data?.observations && (
                      <ul className="grid grid-cols-2 gap-2 text-xs font-medium text-slate-800">
                        {infoSlide.data.observations.map((obs: string, oIdx: number) => (
                          <li key={oIdx} className="p-2 bg-slate-50 border border-slate-200 rounded-lg">
                            "{obs}"
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* FOOTER / PAGE END */}
      <div className="mt-12 pt-6 border-t-2 border-slate-900 text-center text-[9px] font-bold text-slate-400 uppercase tracking-widest print-no-cut">
        Diamond School of Nursing & Clinical Registry Hub — End of Printed Study Material
      </div>
    </div>
  );
};
